import { useQuery } from '../use-query';
import { extendContainerValidator } from '../../../utils';
import { repairService } from '../../../services';
import { CURRENCIES, REPAIRS_GROUP_MOCK_DATA } from '../../../const';
import { useStoreTutorial } from '../../../context';

export const useRepairsQuery = (keys: any = {}) => {
  if (keys.number?.length < 3 || !extendContainerValidator(keys.number)) delete keys.number;

  return useQuery(
    ['repairs', JSON.stringify(keys)],
    () => {
      return repairService
        .getAll({
          params: keys,
        })
        .then((res) => res.data);
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true },
  );
};

export const useRepairsGroupQuery = ({ convertCurrency, ...keys }: any = {}) => {
  const { tutorial } = useStoreTutorial();

  if (keys.number?.length < 3 || !extendContainerValidator(keys.number)) delete keys.number;
  const isLoadingConverter = convertCurrency('USD', 'RUB', 1) === 1;

  return useQuery(
    ['repairs', 'group', JSON.stringify(keys), JSON.stringify(tutorial)],
    async () => {
      if (tutorial.isActive)
        return Promise.resolve({ data: [REPAIRS_GROUP_MOCK_DATA], totalCount: 1 });

      const repairs = await repairService
        .getAllGroup({
          params: keys,
        })
        .then((res) => res.data);

      repairs.data = repairs.data.map((item) => {
        const defaultCurrencyCode = item.repair[0].currencyCode;
        let price = item.repair.reduce((prev, curr) => {
          if (curr.status === 'REJECTED') return prev;
          return prev + convertCurrency(curr.currencyCode, defaultCurrencyCode, curr.price);
        }, 0);
        price = Number(price.toFixed(2));

        return {
          ...item,
          customRepair: {
            activeCount: item.repair.filter((obj) => obj.status === 'ACTIVE').length,
            pendingCount: item.repair.filter((obj) => obj.status === 'PENDING').length,
            uncreatedInvoiceCount: item.repair.filter(
              (obj) => obj.status === 'COMPLETED' && !obj.invoiceLineId,
            ).length,
            price: price + CURRENCIES[defaultCurrencyCode].symbol,
          },
        };
      });

      return repairs;
    },
    {
      initialData: { data: [], totalCount: 0 },
      keepPreviousData: true,
      enabled: !isLoadingConverter,
    },
  );
};

export const useContainerRepairsQuery = (containerId: string, enabled = true) => {
  const query = useQuery(
    ['repairs', JSON.stringify({ containerId })],
    () => {
      if (!containerId) return { data: [], totalCount: 0 };

      return repairService
        .getAll({
          params: { containerId },
          headers: { 'X-Page-Url': 'none' },
        })
        .then((res) => res.data);
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true, enabled },
  );

  return query.data?.data || [];
};

export const useRepairQuery = (id = '') =>
  useQuery(
    ['repair', id],
    () => {
      return repairService.get(id).then((res) => res.data.data);
    },
    {
      enabled: !!id,
    },
  );
